import { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import wsManager from "../../services/wsManager";
import IconSend from '../../components/icons/IconSend';
import IconCloseX from '../../components/icons/IconCloseX';
import axios from "axios";
import language from '../../variables/language';
import DateInput from '../forms/DateInput';
import TimeInput from '../forms/TimeInput';
import LessonCreateModal from '../../pages/students/LessonCreateModal';
import '../../css/phrase-composer.css';

const BUTTON_DATETIME_FORMAT = 'dd.MM.yyyy HH:mm';

// Always docked bottom-right, fixed to the viewport, above everything else
// (.floating-chat-container - see modals.css) - previously every caller
// passed its own `position` variant (left-chat-cont-position/
// left-middle-chat-cont-position/default), each `position:absolute`
// against whatever ancestor happened to establish one, so the popup landed
// in a different spot depending on which page opened it. One fixed
// position everywhere, no `position` prop needed anymore.
export default function ChatComponent({
          chatId, studentId, clientId,
          closeCallback, readedCallback}) {

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [toolsOpen, setToolsOpen] = useState(false);
  const [buttonRows, setButtonRows] = useState([]);
  const [lessonPrefill, setLessonPrefill] = useState(null);
  const [createLessonOpen, setCreateLessonOpen] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [username, setUsername] = useState("");
  const [autorizedChatId, setAutorizedChatId] = useState();
  const messagesEndRef = useRef(null);
  const wsRef = useRef({ topic: null, handler: null });
  const isInited = useRef(false);
  const [closed , setClosed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  // the studentId PROP only exists when the caller already knew it (e.g.
  // StudentCard.jsx) - entry points that only have chatId (e.g. the
  // dashboard's ChatesContainer.jsx chat widget) can still be a student's
  // chat underneath, just not labeled as one by the caller. /api/chats/init
  // resolves this from the actual linked OuterUser.studentId regardless of
  // which param opened it, so prefer that over the prop wherever available.
  const [resolvedStudentId, setResolvedStudentId] = useState(null);
  const effectiveStudentId = studentId || resolvedStudentId;

  useEffect(() => {

    if (chatId == -1 || studentId == -1 || clientId == -1) return;

    if (isInited.current) return;
    isInited.current = true;

    axios.get(`/api/chats/init?` +
      `chatId=${chatId ? chatId : ""}&` +
      `studentId=${studentId ? studentId : ""}&` +
      `clientId=${clientId ? clientId : ""}`, {
      withCredentials: true,
    }).then(res => {
      if (res.data.chatId) {
        setAutorizedChatId(res.data.chatId);
        setName(res.data.name);
        setSurname(res.data.surname);
        setUsername(res.data.username);
        setResolvedStudentId(res.data.studentId || null);
      }
    }).catch(err => {
      console.log(err);
    }).finally( () =>{
      setIsLoaded(true);
    })

  }, [studentId, clientId, chatId])

  useEffect(() => {

    if (!autorizedChatId) return;

    const topic = `/topic/chat/${autorizedChatId}`;

    const handler = (event) => {

      if (event.type === "history") {
        setMessages(event.payload);
        setChatReaded();
      }

      if (event.type === "message") {
        setMessages(prev => [...prev, event.payload]);
      }

    };

    wsManager.subscribe(topic, handler);
    wsRef.current = { topic, handler };
    return () => {
      wsManager.unsubscribe(topic, handler);
    };

  }, [autorizedChatId]);

  const setChatReaded = () => {
    let url = `/api/chats/read/${autorizedChatId}`
    axios.post(
      url,
      { },
      { withCredentials: true })
      .then(response => {
        if (readedCallback) {
          readedCallback(autorizedChatId);
        }
      })
  }

  const closeChat = () => {
      if (wsRef.current.topic && wsRef.current.handler) {
        wsManager.unsubscribe(wsRef.current.topic, wsRef.current.handler);
      }
      setMessages([]);
      setClosed(true);
      setAutorizedChatId(null);


      if (closeCallback) {
        closeCallback();
      }
  }

  const convertTime = (dateTime) => {
    if (!dateTime) return '';
    const cleaned = dateTime.replace(/\[.*\]/, "");
    const dt = DateTime.fromISO(cleaned).toLocal().toFormat("dd LLL HH:mm");
    return dt;
  }
  const MAX_BUTTON_ROWS = 6;
  const addButtonRow = () => {
    if (buttonRows.length >= MAX_BUTTON_ROWS) return;
    setButtonRows(prev => [...prev, { label: "", buttonType: "URL", value: "", showPicker: false, pickerDate: "", pickerTime: "" }]);
    setToolsOpen(true);
  };
  // a Відповідь/CALLBACK button has no separate "value" concept the teacher
  // needs to think about - what the student sees IS what comes back as the
  // reply, so value always mirrors label (and the composer only shows one
  // input for this type - see the JSX below). URL buttons keep label/value
  // genuinely distinct (button text vs. the link it opens), so this sync
  // only applies to CALLBACK rows.
  const updateButtonRow = (index, field, value) => {
    setButtonRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const next = { ...row, [field]: value };
      if (field === 'label' && next.buttonType === 'CALLBACK') {
        next.value = value;
      }
      if (field === 'buttonType') {
        // switching type: CALLBACK immediately mirrors whatever label is
        // already there; URL clears the old mirrored text so it doesn't
        // linger looking like a real (but wrong) link.
        next.value = value === 'CALLBACK' ? next.label : '';
      }
      return next;
    }));
  };
  const toggleButtonPicker = (index) => {
    setButtonRows(prev => prev.map((row, i) => i === index ? { ...row, showPicker: !row.showPicker } : row));
  };
  // picking a date+time auto-fills label AND value (always identical for a
  // CALLBACK button - see updateButtonRow above) with the SAME string: a
  // machine-parseable "dd.MM.yyyy HH:mm" prefix the "create lesson from this
  // reply" feature later extracts via regex, plus a human-readable weekday
  // suffix. Both fields stay editable by hand afterward (there's only one
  // visible input for CALLBACK rows, so editing it edits both at once).
  const updateButtonDateTime = (index, field, value) => {
    setButtonRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const next = { ...row, [field]: value };
      if (next.pickerDate && next.pickerTime) {
        const dt = DateTime.fromFormat(next.pickerDate, 'yyyy-MM-dd');
        const weekdaySuffix = dt.isValid ? ' (' + lang.weekDaysFull[dt.weekday - 1] + ')' : '';
        const datePart = dt.isValid ? dt.toFormat('dd.MM.yyyy') : next.pickerDate;
        const formatted = datePart + ' ' + next.pickerTime + weekdaySuffix;
        next.label = formatted;
        next.value = formatted;
      }
      return next;
    }));
  };
  const removeButtonRow = (index) => {
    setButtonRows(prev => prev.filter((_, i) => i !== index));
  };

  const sendMessage = () => {
    if (!input.trim()) return;

    // only rows with both a label and a value are real buttons - a half-filled
    // row the teacher never finished typing is silently dropped rather than
    // sent as a broken button. Mapped down to just the 3 fields the backend
    // expects - showPicker/pickerDate/pickerTime are composer-only state.
    const validButtons = buttonRows
      .filter(row => row.label.trim() && row.value.trim())
      .map(row => ({ label: row.label.trim(), buttonType: row.buttonType, value: row.value.trim() }));

    const message = {
      chatId: autorizedChatId,
      message: input,
      isBot:1,
    };
    if(validButtons.length > 0) {
      message.buttons = validButtons;
    }
    wsManager.send("/app/chat", message);

    setInput("");
    setButtonRows([]);
    setToolsOpen(false);
  };

  // a CALLBACK button's value always mirrors its label (see updateButtonRow/
  // updateButtonDateTime below), so a lesson-time offer is recognized by
  // extracting a "dd.MM.yyyy HH:mm" pattern from anywhere inside that shared
  // text (updateButtonDateTime puts it at the front, with a human-readable
  // weekday suffix after it) - no separate "is this a time button" flag on
  // the model, the presence of that pattern is the signal.
  const parseTappedButtonDateTime = (tappedButton) => {
    if (!tappedButton || !tappedButton.value) return null;
    const match = tappedButton.value.match(/(\d{2}\.\d{2}\.\d{4})\s+(\d{2}:\d{2})/);
    if (!match) return null;
    const dt = DateTime.fromFormat(match[1] + ' ' + match[2], BUTTON_DATETIME_FORMAT);
    return dt.isValid ? dt : null;
  };
  const openCreateLessonFromTap = (tappedButton) => {
    const dt = parseTappedButtonDateTime(tappedButton);
    if (!dt) return;
    setLessonPrefill({
      date: dt.toFormat('yyyy-MM-dd'),
      start: dt.toFormat('HH:mm'),
      end: dt.plus({ minutes: 60 }).toFormat('HH:mm'),
    });
    setCreateLessonOpen(true);
  };
  useEffect(() => {
    if (messagesEndRef.current) {
        messagesEndRef.current.scrollTop =
        messagesEndRef.current.scrollHeight;
    }
  }, [messages]);

  if (closed) return '';

  const lang = language[language.lang];
  const initials = ((name || '?').charAt(0) + (surname || '').charAt(0)).toUpperCase();

  return (
    <>
    {autorizedChatId ? (
    <div key={autorizedChatId} className="floating-chat-container">
      <div className="floating-chat-header">
        <div className="floating-chat-header-identity">
          <span className="floating-chat-avatar">{initials}</span>
          <div className="floating-chat-header-text">
            <p className="floating-chat-header-name">{name} {surname}</p>
            {username ? <p className="floating-chat-header-username">Telegram: @{username}</p> : ''}
          </div>
        </div>
        <span className="floating-chat-close-btn" onClick={closeChat} title="Закрити">
          <IconCloseX className='floating-chat-close-icon' />
        </span>
      </div>

      <div className="floating-chat-messages" ref={messagesEndRef}>
          {
            messages && Array.isArray(messages) && messages.length > 0 ? (
              messages.map(item => (
                item.isBot == 1 ?
                  <div key={item.id} className='floating-chat-row floating-chat-row-bot'>
                    <span className='floating-chat-avatar floating-chat-avatar-bot' title='Bot'>B</span>
                    <div className='floating-chat-bubble floating-chat-bubble-bot'>
                      <p className='floating-chat-bubble-text'>{item.text}</p>
                      {item.buttons && item.buttons.length > 0 ? (
                        <div className='floating-chat-bubble-buttons'>
                          {item.buttons.map(b => (
                            <span className={'floating-chat-bubble-button-chip' + (b.tapped ? ' tapped' : '')} key={b.id}>
                              <span className='material-symbols-outlined'>{b.buttonType === 'URL' ? 'link' : 'reply'}</span>
                              {b.label}
                              {b.tapped ? <span className='material-symbols-outlined floating-chat-bubble-button-tapped-icon'>check</span> : ''}
                            </span>
                          ))}
                        </div>
                      ) : ''}
                      <span className='floating-chat-bubble-time'>{convertTime(item.dateTime)}</span>
                    </div>
                  </div>
                  :
                  <div key={item.id} className='floating-chat-row floating-chat-row-user'>
                    <div className='floating-chat-bubble floating-chat-bubble-user'>
                      <p className='floating-chat-bubble-text'>{item.text ? item.text : item.message}</p>
                      <span className='floating-chat-bubble-time'>{convertTime(item.dateTime)}</span>
                    </div>
                    {effectiveStudentId && parseTappedButtonDateTime(item.tappedButton) ? (
                      <span className='floating-chat-create-lesson-btn'
                        title={`Створити заняття для ${name} на ${parseTappedButtonDateTime(item.tappedButton).toFormat('dd.MM HH:mm')} (60 хв)`}
                        onClick={() => openCreateLessonFromTap(item.tappedButton)}>
                        <span className='material-symbols-outlined'>event_available</span>
                      </span>
                    ) : ''}
                    <span className='floating-chat-avatar floating-chat-avatar-user' title={name}>{initials}</span>
                  </div>
              ))
            ) : (<p className='floating-chat-empty-hint'>Ще немає повідомлень</p>)
          }
      </div>

      {toolsOpen ? (
        <div className='floating-chat-tools-panel'>
          <div className='floating-chat-tools-header'>
            <span>Кнопки повідомлення</span>
            {buttonRows.length < MAX_BUTTON_ROWS ? (
              <span className='floating-chat-tools-add' onClick={addButtonRow}>
                <span className='material-symbols-outlined'>add</span> Додати кнопку
              </span>
            ) : ''}
          </div>
          {buttonRows.length === 0 ? (
            <p className='floating-chat-tools-empty'>Додайте кнопку, щоб прикріпити її до повідомлення</p>
          ) : buttonRows.map((row, idx) => (
            <div className='floating-chat-button-row-wrap' key={idx}>
              <div className='floating-chat-button-row'>
                <div className='phrase-type-tabs floating-chat-button-type-tabs'>
                  <span className={'phrase-type-tab' + (row.buttonType === 'URL' ? ' active' : '')}
                    onClick={() => updateButtonRow(idx, 'buttonType', 'URL')}>Посилання</span>
                  <span className={'phrase-type-tab' + (row.buttonType === 'CALLBACK' ? ' active' : '')}
                    onClick={() => updateButtonRow(idx, 'buttonType', 'CALLBACK')}>Відповідь</span>
                </div>
                <input className='floating-chat-button-input' placeholder='Текст кнопки'
                  value={row.label} onChange={(e) => updateButtonRow(idx, 'label', e.target.value)} />
                {row.buttonType === 'URL' ? (
                  <input className='floating-chat-button-input' placeholder='https://...'
                    value={row.value} onChange={(e) => updateButtonRow(idx, 'value', e.target.value)} />
                ) : ''}
                <span className={'floating-chat-button-datetime-toggle' + (row.showPicker ? ' active' : '')}
                  onClick={() => toggleButtonPicker(idx)} title='Вибрати дату й час заняття'>
                  <span className='material-symbols-outlined'>event</span>
                </span>
                <span className='floating-chat-button-remove' onClick={() => removeButtonRow(idx)}>
                  <IconCloseX className='floating-chat-button-remove-icon' />
                </span>
              </div>
              {row.showPicker ? (
                <div className='floating-chat-button-datetime-row'>
                  <DateInput className='floating-chat-button-date-input' value={row.pickerDate}
                    onChange={(value) => updateButtonDateTime(idx, 'pickerDate', value)} />
                  <TimeInput className='floating-chat-button-time-input' value={row.pickerTime}
                    onChange={(value) => updateButtonDateTime(idx, 'pickerTime', value)} />
                </div>
              ) : ''}
            </div>
          ))}
        </div>
      ) : ''}

      <div className="floating-chat-input-row">
        <span className={'floating-chat-tools-btn' + (toolsOpen ? ' active' : '')}
          onClick={() => setToolsOpen(o => !o)} title='Панель інструментів'>
          <span className='material-symbols-outlined'>widgets</span>
        </span>
        <input className="floating-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Напишіть повідомлення..."
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
        <span className="floating-chat-send-btn" onClick={sendMessage}>
          <IconSend fillColor='icon-orange' style={{rotate:"-45deg"}} />
        </span>
      </div>

    </div>
    ) : isInited && isLoaded? (
      <div className="floating-chat-container floating-chat-empty-state">
        <span className="floating-chat-close-btn" onClick={closeChat} title="Закрити">
          <IconCloseX className='floating-chat-close-icon' />
        </span>
        <p>Цей клієнт/студент ще не привʼязаний до жодного зовнішнього месенджера</p>
      </div>
    ) : ''}

    <LessonCreateModal isOpen={createLessonOpen} studentId={effectiveStudentId}
      onClose={() => setCreateLessonOpen(false)}
      onCreated={() => setCreateLessonOpen(false)}
      initialDate={lessonPrefill ? lessonPrefill.date : ''}
      initialStart={lessonPrefill ? lessonPrefill.start : ''}
      initialEnd={lessonPrefill ? lessonPrefill.end : ''}
      overlayClassName="modal-above-floating-chat" />
    </>
  );
}
